import { useState } from "react";
import MyTable from "../../Components/MyTable";
import Mytitle from "../../Components/Mytitle";
import ModalForm from "../../Components/ModalForm";
import MyInput from "../../Components/Myinput";
import API, { BASE_URL } from "../../Api/axiosConfig";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import Can from "../../Components/Can";
import { useTranslation } from "react-i18next";
import { SquarePen } from "lucide-react";
import { NegativeNumberDisplay } from "../../utils/formatNegativeNumber";
import { toLocal } from "../../utils/toLocal";



function Customers() {
  const { t } = useTranslation();
  const [imagePreviews, setImagePreviews] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    type: "customer",
    name: "",
    national_id: "",
    phone: null,
    address: "",
    account_number: "",
    bank_name: "",
    opening_balance: "",
    attachments: [],
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editId, setEditId] = useState(null);
  const [canEditOpeningBalance, setCanEditOpeningBalance] = useState(true);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const formatNumberWithColor = (value) => {
      const num = Number(value);
      if (isNaN(num)) return "-";
  
      if (num < 0) {
        // إذا كان الرقم سالبًا: نأخذ القيمة المطلقة، نمررها لـ toLocal لتضع الكومة، ثم نضعها بين قوسين
        const absoluteFormatted = toLocal(Math.abs(num));
        return (
          <span className="text-danger fw-bold" style={{ direction: "ltr", display: "inline-block" }}>
            ({absoluteFormatted})
          </span>
        );
      }
  
      // إذا كان الرقم موجبًا أو صفرًا يظهر بالفواصل بشكل طبيعي
      return <span>{toLocal(num)}</span>;
    };

  const handleAdd = () => {
    setFormData({
      type: "customer",
      name: "",
      national_id: "",
      phone: null,
      address: "",
      account_number: "",
      bank_name: "",
      opening_balance: "",
      attachments: [],
    });
    setEditId(null);
    setErrors({});
    setImagePreviews([]);
    setCanEditOpeningBalance(true);
    setShowModal(true);
  };

  const handleEdit = (record) => {
    const openingBalance =
      record.last_balance?.type === "opening_balance"
        ? record.last_balance.value
        : "";
    setFormData({
      ...record,
      account_number: record.bank_account_number || record.account_number || "",
      opening_balance: openingBalance,
      attachments: [], 
    });
    setEditId(record.uuid);
    setErrors({});
    const hasTransactions =
      record.balances &&
      record.balances.some((b) => b.type !== "opening_balance");
    setCanEditOpeningBalance(!hasTransactions);
    setShowModal(true);

    if (record.attachments && record.attachments.length > 0) {
      const storageBaseUrl = BASE_URL.replace("/api/", "/storage/");
      setImagePreviews(record.attachments.map((att) => ({
        url: att.attachment ? `${storageBaseUrl}${att.attachment}` : att,
        title: att.title || "", // تحميل العنوان القديم إن وجد
        file: null // الصور القديمة من السيرفر لا تملك ملف باينري جديد
      })));

    } else {
      setImagePreviews([]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      
      // هيكلة مصفوفة المعاينة لتصبح كائنات تحمل الملف والاسم
      const newPreviews = files.map((file) => ({
        url: URL.createObjectURL(file),
        title: "", // العنوان يبدأ فارغاً ليكتبه المستخدم
        file: file // الاحتفاظ بالملف الأصلي للإرسال
      }));

      setImagePreviews((prev) => [...prev, ...newPreviews]);
    }
  };

  const removeAttachment = (index) => {
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    // Client-side validation
    let newErrors = {};
    if (!formData.name) newErrors.name = [t("field_required")];

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const data = new FormData();
      
      // 1. ضخ الحقول النصية العادية باستثناء الكيانات والمرفقات القديمة
      Object.keys(formData).forEach((key) => {
        if (key === "attachments") {
          formData.attachments.forEach((file) => {
            data.append("attachments[]", file);
          });
        } else if (
          key === "balances" ||
          key === "last_balance" ||
          (editId && key === "opening_balance" && !canEditOpeningBalance)
        ) {
          // Skip large objects or fields that shouldn't be updated during edit
        } else if (formData[key] !== null && formData[key] !== undefined) {
          data.append(key, formData[key]);
        }
      });

      // 2. 🚀 الحل السحري: ضخ الملفات الثنائية وعناوينها متطابقة بالتوازي في البايلود للـ API
      if (imagePreviews && imagePreviews.length > 0) {
        imagePreviews.forEach((item) => {
          if (item && item.file) {
            data.append("attachments[]", item.file); // الصورة الباينري
            data.append("attachment_names[]", item.title || "بدون عنوان"); // العنوان المدخل للصورة
          }
        });
      }

      if (editId) {
        data.append("_method", "PUT");
        await API.post(`/customers/${editId}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await API.post("/customers", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      setShowModal(false);
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleShowDetails = (customer) => {
    setSelectedCustomer(customer);
    setShowDetailsModal(true);
  };

  const columns = [
    { header: t("customers_name"), accessor: "name" },
    { header: t("national_id"), accessor: "national_id" },
    { header: t("customers_phone"), accessor: "phone" },
    { header: t("address"), accessor: "address" },
    { header: t("account_number"), accessor: "account_number" },
    { header: t("bank_name"), accessor: "bank_name" },
    {
      header: t("customers_currentBalance"),
      accessor: (row) => (
        formatNumberWithColor(row.last_balance?.total_value)
      ),
    },
    {
      header: t("customers_actions"),
      accessor: (row) => {
        return (
          <>
            <div
              style={{ width: "fit-content" }}
              className="d-flex border mx-auto rounded"
            >
              <Can permission="customer_show">
                <button
                  className="btn btn-sm btn-primary mx-1 no-style"
                  onClick={() => handleShowDetails(row)}
                >
                  <FontAwesomeIcon icon={faEye} />
                </button>
              </Can>
              <Can permission="customer_update">
                <button
                  className="btn btn-sm btn-primary mx-1 no-style"
                  onClick={() => handleEdit(row)}
                >
                  <SquarePen />
                </button>
              </Can>
            </div>
          </>
        );
      },
      hideOnPrint: true,
    },
  ];

  return (
    <div className="container mt-5">
      <Mytitle title={t("customers_title")} />

      <ModalForm
        show={showModal}
        onClose={() => setShowModal(false)}
        title={`${editId ? t("customers_editCustomer") : t("customers_addCustomer")} ${formData?.name || ""}`}
        onSubmit={handleSubmit}
        loading={loading}
        mode="form"
      >
        <div className="row">
          <div className="col-md-6">
            <MyInput
              label={t("customers_name")}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={errors.name?.[0]}
              required={true}
            />
          </div>

          <div className="col-md-6">
            <MyInput
              label={t("national_id")}
              value={formData.national_id}
              onChange={(e) => setFormData({ ...formData, national_id: e.target.value })}
              error={errors.national_id?.[0]}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <MyInput
              label={t("customers_phone")}
              type="number"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              error={errors.phone?.[0]}
            />
          </div>

          <div className="col-md-6">
            <MyInput
              label={t("address")}
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              error={errors.address?.[0]}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <MyInput
              label={t("account_number")}
              type="number"
              value={formData.account_number}
              onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
              error={errors.account_number?.[0]}
            />
          </div>

          <div className="col-md-6">
            <MyInput
              label={t("bank_name")}
              value={formData.bank_name}
              onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
              error={errors.bank_name?.[0]}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <MyInput
              label={t("customers_openingBalance")}
              type="number"
              value={formData.opening_balance}
              onChange={(e) => setFormData({ ...formData, opening_balance: e.target.value })}
              error={errors.opening_balance?.[0]}
              disabled={!!(editId && !canEditOpeningBalance)}
            />
            {editId && !canEditOpeningBalance && (
              <div className="text-muted mt-1" style={{ fontSize: "0.9rem" }}>
                {t("cannot_edit_opening_balance_has_transactions") ||
                  "لا يمكن تعديل الرصيد الافتتاحي لوجود معاملات"}
              </div>
            )}
          </div>
        </div>

        {/* ===== قسم المرفقات المطور والمعدل بالكامل ===== */}
        <div className="row mt-4">
          <div className="col-md-12">
            <div className="d-flex align-items-center mb-3 gap-3">
              <h5 className="mb-0 text-white">{t("attachments")}</h5>
              <label className="btn btn-sm btn-outline-primary mb-0" style={{ cursor: "pointer" }}>
                <FontAwesomeIcon icon={faPlus} className="me-1" />
                {t("add")}
                <input
                  type="file"
                  hidden
                  multiple
                  onChange={handleFileChange}
                  accept="image/*"
                />
              </label>
            </div>

            <div className="row">
              {imagePreviews.map((previewObj, index) => (
                <div key={index} className="col-md-6 mb-4">
                  <div 
                    className="position-relative border rounded p-3 d-flex flex-column justify-content-between" 
                    style={{ 
                      height: "240px", 
                      backgroundColor: "#011c1e", 
                      borderColor: "#2b5c56" 
                    }}
                  >
                    {/* زر الحذف العلوي */}
                    <button
                      type="button"
                      className="btn btn-sm btn-danger position-absolute top-0 end-0 m-2"
                      onClick={() => removeAttachment(index)}
                      style={{ borderRadius: "50%", padding: "4px 8px", zIndex: 10 }}
                    >
                      <FontAwesomeIcon icon={faTrash} size="sm" />
                    </button>

                    {/* عرض الصورة المعاينة */}
                    <div className="text-center flex-grow-1 d-flex align-items-center justify-content-center mb-2" style={{ height: "120px" }}>
                      <img
                        src={typeof previewObj === 'object' ? previewObj.url : previewObj}
                        alt={`preview-${index}`}
                        className="img-fluid h-100 rounded"
                        style={{ objectFit: "contain", maxWidth: "100%" }}
                      />
                    </div>

                    {/* حقل إدخال اسم الصورة الجديد */}
                    <div className="w-100">
                      <input
                        type="text"
                        className="form-control text-end"
                        placeholder="عنوان المستند / الصورة"
                        value={previewObj.title || ""}
                        onChange={(e) => {
                          setImagePreviews((prev) =>
                            prev.map((item, idx) =>
                              idx === index ? { ...item, title: e.target.value } : item
                            )
                          );
                        }}
                        style={{
                          backgroundColor: "transparent",
                          border: "2px solid #5a877f",
                          borderRadius: "20px",
                          color: "#fff",
                          padding: "8px 15px",
                          fontSize: "14px",
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </ModalForm>

      <ModalForm
        show={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title={selectedCustomer?.name || ""}
        mode="view"
      >
        {selectedCustomer?.balances?.length ? (
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>{t("customers_details_date")}</th>
                <th>{t("customers_details_type")}</th>
                <th>{t("customers_details_value")}</th>
                <th>{t("customers_details_balance")}</th>
              </tr>
            </thead>
            <tbody>
              {selectedCustomer.balances.map((b) => (
                <tr key={b.id}>
                  <td>{b.date}</td>
                  <td>
                    {b.type === "opening_balance"
                      ? t("customers_details_opening")
                      : t("customers_details_invoice")}
                  </td>
                  <td>
                    {formatNumberWithColor(b.value)}
                  </td>
                  <td>
                    {formatNumberWithColor(b.total_value)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>{t("customers_noPayments")}</p>
        )}

        {selectedCustomer?.attachments?.length > 0 && (
          <div className="mt-4">
            <h5 className="mb-3">{t("attachments")}</h5>
            <div className="row">
              {selectedCustomer.attachments.map((att, index) => {
                const storageBaseUrl = BASE_URL.replace("/api/", "/storage/");
                const imageUrl = att.attachment
                  ? `${storageBaseUrl}${att.attachment}`
                  : att.url || att;
                return (
                  <div key={index} className="col-md-4 mb-3">
                    <div className="border rounded p-2 text-center" style={{ background: "#f8f9fa" }}>
                      
                      {/* 🌟 عرض اسم الصورة الراجع من السيرفر */}
                      <p className="fw-bold small text-dark mb-1 text-truncate" title={att.name}>
                        {att.name || "بدون عنوان"}
                      </p>

                      <div style={{ height: "130px" }}>
                        <a href={imageUrl} target="_blank" rel="noreferrer">
                          <img
                            src={imageUrl}
                            alt={`attachment-${index}`}
                            className="img-fluid h-100"
                            style={{ objectFit: "contain", cursor: "pointer" }}
                          />
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </ModalForm>

      <MyTable
        resource="customers"
        columns={columns}
        refreshKey={refreshKey}
        title={t("customers_title")}
        button={{
          text: t("customers_addCustomer"),
          onClick: handleAdd,
          variant: "success",
          permission: "employee_create",
        }}
        baseFilter={(item) => item.type === "customer"}
      />
      <div style={{ display: "flex", justifyContent: "center", marginTop: "40px" }}>
        <div
          className="text-center"
          style={{
            borderRadius: "15px",
            padding: "12px 35px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            minWidth: "300px",
            maxWidth: "90vw",
            backgroundColor: "#b19bd5",
            color: "#fff",
          }}
        >
          ! <strong>{t("customers_alert_title")}</strong>{" "}
          {t("customers_alert_message")}
        </div>
      </div>
    </div>
  );
}

export default Customers;